import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MdSearch } from 'react-icons/md'
import Navbar from './Navbar'

export default function TransactionHistory() {
  const { transactions = [] } = useSelector(state => state)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredTransactions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) return transactions

    return transactions.filter(transaction =>
      [transaction.email, transaction.reference]
        .some(value => String(value || '').toLowerCase().includes(query))
    )
  }, [transactions, searchQuery])

  const formatCurrency = amount =>
    `₦${Number(amount || 0).toLocaleString()}`

  const formatDate = value => {
    if (!value) return '—'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'

    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7">
            <p className="text-sm font-semibold text-green-600">REPAYMENTS</p>
            <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">Transaction history</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Successful customer repayments recorded by RentHub.
                </p>
              </div>

              <div className="w-fit rounded-full border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-600 shadow-sm">
                {transactions.length} transaction{transactions.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="relative max-w-2xl">
              <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
              <input
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search by customer email or reference..."
                className="h-12 w-full rounded-xl border border-gray-300 pl-11 pr-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
              />
            </div>
          </div>

          {filteredTransactions.length > 0 ? (
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-left text-sm">
                  <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-5 py-4">Customer</th>
                      <th className="px-5 py-4">Amount</th>
                      <th className="px-5 py-4">Reference</th>
                      <th className="px-5 py-4">Date</th>
                      <th className="px-5 py-4">Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredTransactions.map((transaction, index) => (
                      <tr
                        key={transaction.id || transaction.reference || index}
                        className="border-t border-gray-100 transition hover:bg-gray-50/80"
                      >
                        <td className="px-5 py-4 font-semibold text-gray-900">
                          {transaction.email || '—'}
                        </td>
                        <td className="px-5 py-4 font-bold text-gray-900">
                          {formatCurrency(transaction.amount)}
                        </td>
                        <td className="px-5 py-4 font-mono text-xs text-gray-500">
                          {transaction.reference || '—'}
                        </td>
                        <td className="px-5 py-4 text-gray-600">
                          {formatDate(transaction.date || transaction.paid_date)}
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex rounded-full border border-green-100 bg-green-50 px-2.5 py-1 text-[11px] font-bold uppercase text-green-700">
                            Successful
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
              <p className="text-lg font-semibold text-gray-700">No transactions yet</p>
              <p className="mt-2 text-sm text-gray-400">Successful repayments will appear here.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
